import React, { ReactElement } from "react";

import styled from "styled-components";

import { EntityDots, StyledTitleBlock } from "./shared";
import { Chip } from "../../../components/Chip";
import { StackedCardRow as StackRow } from "../../../components/StackedCard";
import {
  Body2,
  Caption1,
  Caption2,
  Header3,
} from "../../../components/Typography";
import { RoundAvatar } from "../../shared";

/**
 * ProjectCard — a project as a stacked card.
 *
 * The sixth content type, and the second one that mirrors a project-management
 * record rather than something authored on the surface (TaskCard is the first).
 * It composes the same shell and the same rows; nothing in `components/StackedCard`
 * knows it exists.
 *
 * The face is the app's Projects page, read off `ProjectListGroup.jsx`:
 *   - the NAME is the card's title (`Header3`, as in the grid card);
 *   - the CLIENT sits under it — `Entity.Property "entity-property-client"`, the
 *     company the project is for;
 *   - the DESCRIPTION is the project's `body`, the grid card's `Body2` tertiary
 *     excerpt, clamped because the whole text lives on the project and this is a
 *     reference to it, not a copy;
 *   - the LEADER and the LABEL share one row. The label is a FILLED pill whose
 *     background is the label's own colour and whose text is a darker shade of
 *     it (`ProjectLabel.jsx`: `EntityLabel` with `backgroundColor: label.color`,
 *     `color: label.darker_text_color`) — the design system's `Chip`, not the
 *     colour-text `Tag` the column cards use for task labels.
 *
 * There is deliberately NO progress indicator. A single percentage is the one
 * number on a project that cannot be read without knowing how it was computed,
 * and a card is a place people put a project to talk ABOUT it — the number
 * invites a conversation about the number instead.
 */

export interface ProjectCardPerson {
  name: string;
  initials: string;
  /** Avatar colours stand in for the server-rendered avatar: data, not theme. */
  color: string;
}

/** A project label carries both of the colours the app stores for it. */
export interface ProjectCardLabel {
  text: string;
  /** `label.color` — the pill's background. */
  color: string;
  /** `label.darker_text_color` — the pill's text. */
  textColor: string;
}

/**
 * What an internal project's client line says.
 *
 * A project with no client belongs to the account's own company, and printing
 * that company's name back at someone who works there says nothing. So the line
 * states the fact instead.
 */
export const INTERNAL_CLIENT_LABEL = "Internal";

/** One rule for the client line, so no surface can label the same project twice. */
export const projectClientLine = (client: string | null | undefined): string =>
  client ?? INTERNAL_CLIENT_LABEL;

/* The description excerpt. Three lines is the most a card can carry before it
   stops being a card, so it clamps. */
const StyledExcerpt = styled(Body2)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledName = styled(Header3)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledClient = styled(Caption2)`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/**
 * One row for the two facts that are each optional: the leader on the LEFT, the
 * label on the RIGHT. `space-between` is what keeps the label right when there is
 * no leader beside it, and the row is not rendered at all when the project has
 * neither — a bare project produces a small card, not a card with empty
 * furniture in it.
 *
 * No rule above it: the card carries no internal separators. The row's own role
 * padding is what sets it apart from the excerpt, the same way every other row in
 * every other content type is set apart — a hairline here would be the only line
 * inside any card in the set.
 */
const StyledMetaRow = styled(StackRow)`
  justify-content: space-between;

  .sc-leader {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .sc-leader > .typography {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* The Chip brings its own padding and radius, so the slot only has to refuse
     to shrink and stay out of the leader's way. */
  .sc-label {
    flex-shrink: 0;
    display: flex;
  }
`;

export interface ProjectCardProps {
  name?: string;
  /** The client company. `null` is an internal project — the line reads "Internal". */
  client?: string | null;
  /** The project's `body`; the excerpt row exists only when it has one. */
  description?: string;
  /** A project need not have a leader. */
  leader?: ProjectCardPerson;
  /** A project need not have a label. */
  label?: ProjectCardLabel;
}

export const ProjectCard = ({
  name = "Marketing Site",
  client = "ActiveCollab Inc.",
  description,
  leader,
  label,
}: ProjectCardProps): ReactElement => (
  <>
    <StackRow role="title">
      <StyledTitleBlock>
        <StyledName>{name}</StyledName>
        <StyledClient color="tertiary">
          {projectClientLine(client)}
        </StyledClient>
      </StyledTitleBlock>
      <EntityDots aria-label="Project options" />
    </StackRow>

    {description ? (
      <StackRow role="custom">
        <StyledExcerpt color="secondary">{description}</StyledExcerpt>
      </StackRow>
    ) : null}

    {leader || label ? (
      <StyledMetaRow role="footer">
        {leader ? (
          <span className="sc-leader">
            <RoundAvatar
              $bg={leader.color}
              $size={24}
              $bordered={false}
              data-fixed
            >
              {leader.initials}
            </RoundAvatar>
            <Caption1 color="secondary">{leader.name}</Caption1>
          </span>
        ) : (
          /* Keeps the label on the right when there is no leader to push it
             there — an empty flex item is cheaper than a second layout. */
          <span />
        )}
        {label ? (
          <span className="sc-label" data-fixed>
            <Chip
              size="small"
              label={label.text}
              backgroundColor={label.color}
              color={label.textColor}
              typographyProps={{ variant: "Caption 2", weight: "bold" }}
            />
          </span>
        ) : null}
      </StyledMetaRow>
    ) : null}
  </>
);
